/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Button, Cell, Grid, HFlow, Icon, Link, Text, Theme, useTheme, VFlow } from 'bold-ui'
import useSession from 'components/auth/useSession'
import { Breadcrumb } from 'components/breadcrumb/Breadcrumb'
import { Form, FormFooter, FormRenderProps } from 'components/form'
import { PageContent } from 'components/layout/PageContent'
import { RelatoriosButtonGroup } from 'components/relatorios-buttons/RelatoriosButtonGroup'
import { usePagedTableProps } from 'components/table'
import { TitleGroup } from 'components/TitleGroup'
import { useFlags } from 'config/useFlagsContext'
import {
  AcompanhamentoCondicaoSaudeTematicaEnum,
  ArquivoTemporarioIdentificadorEnum,
  ArquivoTemporarioStatusEnum,
  FaixaEtariaEnum,
} from 'graphql/types.generated'
import { useFirebase } from 'hooks/firebase/useFirebase'
import image from 'images/403-forbidden.png'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router'
import { Link as RouterLink } from 'react-router-dom'
import Permissions from 'types/Permissions'
import { isUndefinedOrNull } from 'util/checks'
import { RelatoriosRecentes } from 'view/arquivo/RelatoriosRecentes'
import { LinkVerEquipesVinculadasView } from 'view/vinculacao-equipes/LinkVerEquipesVinculadasView'

import { AcompanhamentoCondicaoSaudeForm } from './AcompanhamentoCondicaoSaudeForm'
import { calculator } from './calculator-acompanhamentoCondicaoSaude'
import { AcompanhamentoCondicaoSaudeTable } from './components/table/AcompanhamentoCondicaoSaudeTable'
import { AcompanhamentoCondicaoSaudeTableHeader } from './components/table/AcompanhamentoCondicaoSaudeTableHeader'
import convertModelToInput from './converter-acompanhamentoCondicaoSaude'
import { DoSearchPanel } from './DoSearchPanel'
import { downloadAcompanhamentoCondicaoSaudeCSV, downloadAcompanhamentoCondicaoSaudePDF } from './downloadRelatorio'
import { useBuscaCidadaoListagem } from './hooks/useBuscaCidadaoListagem'
import { AcompanhamentoCondicaoSaudeFormModel } from './model/model-acompanhamentoCondicaoSaude'
import { validate as validateForm } from './validator-acompanhamentoCondicaoSaude'

// TODO Royal Flush - Filtros Aplicados do Acompanhamento #20112
export default function AcompanhamentoCondicaoSaudeView() {
  const [currentFilter, setCurrentFilter] = useState<AcompanhamentoCondicaoSaudeFormModel>()
  // const [executeQuery, { loading, data }] = useBuscaCidadaoListagemLazyQuery()
  const { ACOMPANHAMENTO_CONDICAO_SAUDE_ENABLED } = useFlags()

  const { analytics } = useFirebase()
  const history = useHistory()
  const { hasAuthorization } = useSession()

  const theme = useTheme()
  const styles = createStyles(theme)

  const [shouldRefetch, setShouldRefetch] = useState(false)
  const [statusRelatorio, setStatusRelatorio] = useState<ArquivoTemporarioStatusEnum>()

  const hasTriaFilter = !isUndefinedOrNull(currentFilter?.tria)
  const hasIvcfFilter = !isUndefinedOrNull(currentFilter?.ivcf)
  const [listaTematica, setListaTematica] = useState<AcompanhamentoCondicaoSaudeTematicaEnum>()

  const { cidadaos, executeQuery, loading } = useBuscaCidadaoListagem(listaTematica)
  const isCoordenador = hasAuthorization(Permissions.acompanhamentos.condicoesDeSaude.todasAsEquipes)

  const identificadorModuloArquivo = ArquivoTemporarioIdentificadorEnum.RELATORIO_ACOMPANHAMENTO_CONDICAO_SAUDE

  const tableProps = usePagedTableProps({
    result: cidadaos,
    loading,
    onChange: setCurrentFilter,
  })

  const decorators = useMemo(() => [calculator], [])

  useEffect(() => {
    currentFilter &&
      executeQuery({
        variables: {
          input: { ...convertModelToInput(currentFilter) },
        },
      })
  }, [currentFilter, executeQuery])

  const afterSubmit = () => {
    setShouldRefetch(true)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleExportarCSVClick = () => {
    analytics.logEvent('exportar_csv_acompanhamento_condicoes_saude')
    downloadAcompanhamentoCondicaoSaudeCSV(convertModelToInput(currentFilter))
    afterSubmit()
  }

  const handleExportarPDFClick = () => {
    analytics.logEvent('exportar_pdf_acompanhamento_condicoes_sa')
    downloadAcompanhamentoCondicaoSaudePDF(convertModelToInput(currentFilter))
    afterSubmit()
  }

  const renderForm = (formProps: FormRenderProps<AcompanhamentoCondicaoSaudeFormModel>) => {
    const { values } = formProps
    const showTable = loading || !isUndefinedOrNull(cidadaos)

    return (
      <Fragment>
        <Breadcrumb title='Acompanhamento de condições de saúde' />
        <VFlow style={styles.pageContent}>
          <PageContent type='filled'>
            <HFlow justifyContent='space-between' style={styles.titleContent}>
              <TitleGroup title='Acompanhamento de condições de saúde' />
              <RelatoriosRecentes
                setShouldRefetch={setShouldRefetch}
                identificadorModuloArquivo={identificadorModuloArquivo}
                setStatusRelatorio={setStatusRelatorio}
              />
            </HFlow>
            <AcompanhamentoCondicaoSaudeForm setListaTematica={setListaTematica} {...formProps} />
          </PageContent>
        </VFlow>
        <PageContent>
          <VFlow>
            {showTable ? (
              <Fragment>
                <AcompanhamentoCondicaoSaudeTableHeader hasTriaFilter={hasTriaFilter} listaTematica={listaTematica} />
                <VFlow>
                  <LinkVerEquipesVinculadasView equipe={currentFilter.equipeResponsavel} />
                  <AcompanhamentoCondicaoSaudeTable
                    {...tableProps}
                    hasIvcfFilter={hasIvcfFilter}
                    tematica={listaTematica}
                  />
                </VFlow>
                <FormFooter>
                  <RelatoriosButtonGroup
                    hasIvcfFilter={hasIvcfFilter}
                    shouldRefetch={shouldRefetch}
                    isAcompanhamentoCondicoesSaude
                    statusRelatorio={statusRelatorio}
                    numberOfElements={cidadaos?.content.length}
                    onClickExportarCSV={handleExportarCSVClick}
                    onClickExportarPDF={handleExportarPDFClick}
                  />
                </FormFooter>
              </Fragment>
            ) : (
              <DoSearchPanel isCoordenador={isCoordenador} equipeResponsavelId={values.equipeResponsavelId} />
            )}
          </VFlow>
        </PageContent>
      </Fragment>
    )
  }

  return (
    <Fragment>
      {ACOMPANHAMENTO_CONDICAO_SAUDE_ENABLED ? (
        <Form<AcompanhamentoCondicaoSaudeFormModel>
          render={renderForm}
          onSubmit={setCurrentFilter}
          validate={validateForm}
          decorators={decorators}
          initialValues={{
            listaTematica: AcompanhamentoCondicaoSaudeTematicaEnum.GERAL,
            tipoFaixaEtaria: FaixaEtariaEnum.TODAS_FAIXAS,
          }}
        />
      ) : (
        <PageContent>
          <Grid style={{ paddingTop: '10rem' }} alignItems='center' justifyContent='space-around'>
            <Cell>
              <VFlow>
                <VFlow vSpacing={0.5}>
                  <h1>Estamos em manutenção</h1>
                  <p>Pedimos desculpas pelo imprevisto, mas não se preocupe, logo a situação será resolvida.</p>
                </VFlow>

                <p>
                  <Button kind='primary' onClick={history.goBack}>
                    <Icon icon='arrowLeft' style={{ marginRight: '0.25rem' }} />
                    <Text color='inherit'>Retornar à página anterior</Text>
                  </Button>
                </p>

                <p>
                  <Link component={RouterLink} to='/'>
                    Acessar página inicial
                  </Link>
                </p>
              </VFlow>
            </Cell>
            <Cell>
              <img
                src={image}
                alt='Duas pessoas olhando para uma porta protegida por um lacre, representando que o acesso está proibido'
              />
            </Cell>
          </Grid>
        </PageContent>
      )}
    </Fragment>
  )
}

const createStyles = (theme: Theme) => ({
  titleContent: css`
    margin: 1rem 0;
  `,
  pageContent: css`
    border-bottom: 1px solid ${theme.pallete.divider};
  `,
})
